import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { navigateToGLDGame } from "../../../services/gameNavigationService";
import { trackEvent } from "../../../shared/services/playerEvents";

/**
 * FirstRunActivationModal: treatment interstitial for the first-run activation
 * experiment (feature 22, WS-3).
 *
 * Shows ONLY when the resolve endpoint bucketed the player to `treatment`
 * (decided in useFirstRunActivation). Control players never see it. It is a
 * one-tap nudge into a FREE-PLAY (Gold Coins) game; it never touches Cluck
 * Bucks (Cluck Bucks are never referenced), and is always dismissible (no trap):
 * X, backdrop tap, Escape, and "Maybe later" all close it, and the hook
 * remembers the dismissal for the session.
 *
 * Copy is server-provided and compliance-checked (grant_copy names free Gold
 * Coins only, no em dashes). Renders as a bottom sheet on mobile and a centered
 * dialog on desktop, both from the existing shared UI (no new dependency).
 *
 * THE WHEEL LINE. Free play stays the primary action: it is the arm that won,
 * it costs the player nothing, and leading with anything that needs phone
 * verification would front-load the exact friction that collapsed deposits on
 * 2026-08-05. But the nudge used to end there, and a new player was never told
 * that the calendar opens on a wheel carrying real Cluck Bucks. "Is there no
 * sign up bonus? Or free sc??" and "are we supposed to receive CB as a welcome
 * bonus?" are among the most common things players ask support, which is a
 * first-run explanation failing, not a support problem. The wheel is offered
 * as a clearly secondary path, only when the server confirms it is really
 * there, and with no amount and no mention of chances.
 *
 * INSTRUMENTATION. This surface previously emitted nothing at all, so its
 * effect on activation was unmeasurable while every neighbouring funnel (phone
 * gate, checkout, card) was tracked. Shown / spin / wheel / dismissed now fire,
 * and dismissal carries how it was dismissed so "Maybe later" can be told apart
 * from a backdrop tap.
 */
const FirstRunActivationModal = ({ open, treatment, onDismiss, isMobile }) => {
  const navigate = useNavigate();
  const seenRef = useRef(false);

  const gameCode = treatment?.game_code || null;
  const wheelDay = treatment?.welcome_wheel?.day ?? null;

  // One `first_run_shown` per mount, fired on the real open transition rather
  // than on render, so a re-render never inflates the denominator.
  useEffect(() => {
    if (!open || !gameCode || seenRef.current) return;
    seenRef.current = true;
    trackEvent("first_run_shown", {
      game_code: gameCode,
      welcome_wheel: wheelDay !== null,
    });
  }, [open, gameCode, wheelDay]);

  if (!treatment?.game_code) return null;

  // Server-provided, compliance-checked strings. Fall back to a safe, generic
  // free Gold Coins phrasing if the server omitted the copy.
  const grantCopy = treatment.grant_copy || "free Gold Coins";
  const gameName = treatment.game_name || "";

  const handleSpin = () => {
    trackEvent("first_run_spin_clicked", { game_code: treatment.game_code });
    // Mark dismissed first so the modal never re-appears when the player
    // navigates back to the lobby, then deep-link into free play.
    onDismiss?.();
    navigateToGLDGame(navigate, { game_code: treatment.game_code });
  };

  const handleWheel = () => {
    trackEvent("first_run_wheel_clicked", { day: wheelDay });
    onDismiss?.();
    // The claim itself raises the phone sheet if it is needed. Sending them to
    // the calendar rather than explaining verification up front keeps the
    // friction where it belongs, at the moment of the claim.
    navigate("/offers");
  };

  // Radix calls onOpenChange(false) for X / backdrop / Escape. Route them all
  // through the single dismissal path.
  const handleOpenChange = (next) => {
    if (!next) {
      trackEvent("first_run_dismissed", { via: "backdrop_or_escape" });
      onDismiss?.();
    }
  };

  const handleMaybeLater = () => {
    trackEvent("first_run_dismissed", { via: "maybe_later" });
    onDismiss?.();
  };

  const body = (
    <div className="flex flex-col items-center text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/15">
        <img
          src="/Logo-symbol.png"
          alt="Cluck"
          className="h-10 w-10 object-contain"
        />
      </div>

      <p className="text-2xl font-bold text-white">You have {grantCopy}</p>
      <p className="mt-2 text-sm text-muted-foreground">
        {gameName
          ? `Jump into ${gameName} and play your first game on us.`
          : "Ready to play your first game? It is on the house."}
      </p>

      <div className="mt-6 flex w-full flex-col gap-2">
        <Button
          variant="gold"
          size="lg"
          onClick={handleSpin}
          className="w-full"
          autoFocus
        >
          <Sparkles className="h-5 w-5" aria-hidden="true" />
          Spin now
        </Button>

        {/* Only when the server confirmed day one really is a Cluck Bucks
            wheel. No amount and no odds: both are protected. */}
        {wheelDay !== null && (
          <>
            <p className="mt-1 text-sm text-muted-foreground">
              Your sign-in calendar also starts with the Welcome Wheel, and it
              lands Cluck Bucks. It is free, and no purchase is needed.
            </p>
            <Button
              variant="outline"
              size="lg"
              onClick={handleWheel}
              className="w-full"
            >
              Go to my Welcome Wheel
            </Button>
          </>
        )}

        <Button
          variant="ghost"
          size="lg"
          onClick={handleMaybeLater}
          className="w-full text-muted-foreground"
        >
          Maybe later
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl motion-reduce:animate-none"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>You have {grantCopy}</SheetTitle>
            <SheetDescription>
              Play your first game with your free Gold Coins.
            </SheetDescription>
          </SheetHeader>
          <div className="pt-2">{body}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="motion-reduce:animate-none">
        <DialogHeader className="sr-only">
          <DialogTitle>You have {grantCopy}</DialogTitle>
          <DialogDescription>
            Play your first game with your free Gold Coins.
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
};

export default FirstRunActivationModal;
